import React, { useEffect, useState } from 'react';

import { Box, Button, Card, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import NotificationMessageTemplateAutoComplete from 'components/form-fields/auto-complete-fields/NotificationMessageTemplateAutoComplete';
import { Flow, FlowTriggerList } from 'types';
import { camelCaseToTitleCaseText } from 'utils/formatters';

import { useStyles } from '../styles';
import {
  CardTypes,
  FormValues,
  NotificationMessageTemplateAutoCompleteValue,
  notificationTypeValues,
  recipientsValues,
  TriggerTypes,
} from '../types';
// eslint-disable-next-line import/order
import FlowBuilderDeliveryTypeContainer from './FlowBuilderDeliveryTypeContainer';
// eslint-disable-next-line import/order
import FlowTriggerSearchList from './FlowTriggerSearchList';

interface IFlowBuilderSidebarProps {
  selectedCard: CardTypes;
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
  handleNotificationMessageTemplateChange: (
    item: NotificationMessageTemplateAutoCompleteValue | null,
  ) => void;
  handleCreateNotificationMessageTemplate: () => void;
  handleOptionSelected: (cardType: CardTypes) => void;
  permission?: boolean;
  triggerList: FlowTriggerList | undefined;
  selectedTriggerType: TriggerTypes;
  handleSelectedTrigger: (value: Flow['trigger']) => void;
  selectedDeliveryMode: Flow['deliveryMode'];
  selectedTimeUnit: Flow['timeUnit'];
  selectedTimeAmount: number | null;
  sendAtTime: string | null;
  handleDeliveryModeChange: (value: Flow['deliveryMode']) => void;
  handleTimeUnitChange: (value: Flow['timeUnit']) => void;
  handleTimeAmountChange: (value: number) => void;
  handleSendAtTimeChange: (value: string | null) => void;
  isSmsEnabled: boolean;
}

const FlowBuilderSidebar = ({
  selectedCard,
  form,
  handleNotificationMessageTemplateChange,
  handleCreateNotificationMessageTemplate,
  handleOptionSelected,
  triggerList,
  permission = true,
  selectedTriggerType,
  handleSelectedTrigger,
  selectedDeliveryMode,
  selectedTimeUnit,
  selectedTimeAmount,
  sendAtTime,
  handleDeliveryModeChange,
  handleTimeUnitChange,
  handleTimeAmountChange,
  handleSendAtTimeChange,
  isSmsEnabled,
}: IFlowBuilderSidebarProps): JSX.Element => {
  const [choicesList, setChoicesList] = useState<string[]>([]);
  const { classes } = useStyles();
  const theme = useMantineTheme();

  useEffect(() => {
    if (selectedCard === 'trigger') {
      setChoicesList(
        triggerList
          ? triggerList.immediateAndDelayedTriggerList.concat(triggerList.reminderTriggerList)
          : [''],
      );
    } else if (selectedCard === 'recipients') {
      if (form.values.trigger) {
        const recipientsChoices = [];
        if (!triggerList?.athletesRecipientDisallowedTriggerList.includes(form.values.trigger)) {
          recipientsChoices.push('Athletes');
        }
        if (!triggerList?.coachesRecipientDisallowedTriggerList.includes(form.values.trigger)) {
          recipientsChoices.push('Coaches');
        }
        recipientsChoices.push('Staff');
        setChoicesList(recipientsChoices);
      } else {
        setChoicesList(recipientsValues);
      }
    } else if (selectedCard === 'notificationType') {
      setChoicesList(isSmsEnabled ? notificationTypeValues : ['Email']);
    }
  }, [selectedCard, triggerList, form.values.trigger, isSmsEnabled]);

  const handleCardClick = (choice: string): void => {
    if (permission) {
      if (selectedCard === 'trigger') {
        handleSelectedTrigger(choice as Flow['trigger']);
      } else if (selectedCard === 'recipients') {
        form.setFieldValue('recipients', choice as Flow['recipients']);
      } else if (selectedCard === 'notificationType' && form.values.notificationType != choice) {
        form.setFieldValue('notificationType', choice as Flow['notificationType']);
        form.setFieldValue('notificationMessageTemplate', {
          notificationMessageTemplateId: '',
          value: '',
          label: '',
        });
      }
      handleOptionSelected(selectedCard);
    }
  };

  return (
    <Paper withBorder shadow='xs' p='md' style={{ height: '100%', overflow: 'auto' }}>
      <Box
        sx={{
          textAlign: 'center',
          marginBottom: '20px',
        }}
      >
        {selectedCard && <Title order={4}>Select {camelCaseToTitleCaseText(selectedCard)}</Title>}
      </Box>
      <Stack spacing='md' align='center' w={'100%'}>
        {selectedCard === 'notificationMessageTemplate' ? (
          <>
            <NotificationMessageTemplateAutoComplete
              label='Select Notification Message Template'
              value={form.values.notificationMessageTemplate}
              onChange={handleNotificationMessageTemplateChange}
              notificationType={form.values.notificationType}
            />
            <Text> -- or -- </Text>
            <Button
              leftIcon={<IconPlus size={theme.fontSizes.md} />}
              onClick={handleCreateNotificationMessageTemplate}
              data-cy='createNotificationMessageTemplateButton'
              disabled={!permission}
            >
              Create a Notification Message Template
            </Button>
          </>
        ) : selectedCard === 'trigger' ? (
          <FlowTriggerSearchList
            handleCardClick={handleCardClick}
            choicesList={choicesList}
            descriptions={triggerList?.triggerDescriptions}
          />
        ) : selectedCard === 'delivery' ? (
          <FlowBuilderDeliveryTypeContainer
            selectedTriggerType={selectedTriggerType}
            selectedDeliveryMode={selectedDeliveryMode}
            selectedTimeAmount={selectedTimeAmount}
            selectedTimeUnit={selectedTimeUnit}
            selectedTrigger={form.values.trigger}
            sendAtTime={sendAtTime}
            handleDeliveryModeChange={handleDeliveryModeChange}
            handleTimeAmountChange={handleTimeAmountChange}
            handleTimeUnitChange={handleTimeUnitChange}
            handleSendAtTimeChange={handleSendAtTimeChange}
            isHourUnitAllowed={
              form.values.trigger && triggerList?.hourUnitAllowedTriggerList.includes(form.values.trigger)
                ? true
                : false
            }
          />
        ) : (
          choicesList.map(choice => (
            <Card
              className={classes.clickableCard}
              key={choice}
              shadow='sm'
              p='lg'
              radius='md'
              withBorder
              style={{ width: '90%', maxWidth: '300px' }}
              onClick={() => handleCardClick(choice)}
            >
              <Text>{choice}</Text>
            </Card>
          ))
        )}
      </Stack>
    </Paper>
  );
};

export default FlowBuilderSidebar;
